import React, { useState, useEffect, useRef, useMemo } from "react";
import { useMediaQuery } from "react-responsive";
import styled, { css, keyframes } from "styled-components";

/* Skeleton variants — silhouette of the real items. Card body breathes
   between two close shades (no shimmer sweep, no glints) so a long
   wait reads as "loading" without the busy diagonal animation that the
   shimmer sweep produced.
   • "explore"    → asymmetric puddle border-radius
   • "collection" → 18px rounded frame */

/* Shade pulse uses the glass triplet tokens so it flips in dark theme:
   light → bright water; dark → deep navy that matches the page. */
const shadePulse = keyframes`
  0%, 100% {
    background:
      radial-gradient(
        120% 90% at 20% 0%,
        rgba(var(--glass-white-rgb), 0.78) 0%,
        rgba(var(--glass-pale-rgb), 0.62) 45%,
        rgba(var(--glass-pale-rgb), 0.5) 100%
      );
  }
  50% {
    background:
      radial-gradient(
        120% 90% at 20% 0%,
        rgba(var(--glass-white-rgb), 0.92) 0%,
        rgba(var(--glass-pale-rgb), 0.78) 45%,
        rgba(var(--glass-pale-rgb), 0.66) 100%
      );
  }
`;

const VideoMasonrySkeletonLoader = ({
  theme = "water",
  count = 12,
  isCollectionPage = false,
  mobileTwoRows,
  fullWidth,
  variant = "explore",
}) => {
  const [videoWidth, setVideoWidth] = useState(244);
  const [masonryColumns, setMasonryColumns] = useState([]);
  const containerRef = useRef();

  const isDesktop = useMediaQuery({ maxWidth: 1440 });
  const isLargeDesktop = useMediaQuery({ maxWidth: 1600 });
  const isTable = useMediaQuery({ maxWidth: 950 });
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const numOfColumns = useMemo(() => {
    switch (true) {
      case isMobile:
        return 2;
      case isTable:
        return 3;
      case isDesktop:
        return 4;
      case isLargeDesktop:
        return 5;
      default:
        return 6;
    }
  }, [isLargeDesktop, isDesktop, isTable, isMobile]);

  useEffect(() => {
    const updateLayout = () => {
      const containerPadding = 32;
      const gap = 4;
      const availableWidth = window.innerWidth - containerPadding;
      const calculatedWidth = Math.floor(
        (availableWidth - (numOfColumns - 1) * gap) / numOfColumns,
      );
      setVideoWidth(Math.max(140, calculatedWidth));
    };

    updateLayout();
    window.addEventListener("resize", updateLayout);
    return () => window.removeEventListener("resize", updateLayout);
  }, [isCollectionPage, fullWidth, numOfColumns, mobileTwoRows]);

  useEffect(() => {
    const spacing = 4;
    const columns = Array.from({ length: numOfColumns }, () => []);
    const columnHeights = Array(numOfColumns).fill(0);

    const mockItems = Array.from({ length: count }, (_, i) => ({
      _id: `skeleton-${i}`,
      id: i,
    }));

    mockItems.forEach((item, index) => {
      const aspectRatios = [9 / 6, 3 / 2, 1 / 1];
      const aspectRatio = aspectRatios[index % aspectRatios?.length];
      const height = Math.round(videoWidth * aspectRatio);

      const shortestColumnIndex = columnHeights.indexOf(
        Math.min(...columnHeights),
      );

      columns[shortestColumnIndex].push({
        ...item,
        height: height,
        width: videoWidth,
        aspectRatio: aspectRatio,
      });

      columnHeights[shortestColumnIndex] += height + spacing;
    });

    setMasonryColumns(columns);
  }, [count, numOfColumns, videoWidth]);

  const isCollection = variant === "collection";

  /* Minimal skeleton — just the card silhouettes. No shimmer sweeps, no
     glints, no per-card overlays. The previous build stacked 6+ keyframe
     animations per tile and a brightness keyframe on every container,
     which on a phone meant 100+ parallel animations on first paint. The
     real cards land within ~200ms; users don't need a frosted simulation
     of the full UI in the meantime. */
  return (
    <MasonryContainer ref={containerRef}>
      <MasonryGrid>
        {masonryColumns.map((column, columnIndex) => (
          <MasonryColumn key={columnIndex}>
            {column.map((item) => (
              <MasonryItem key={`skeleton-${item.id}`}>
                <SkeletonItem
                  $collection={isCollection}
                  style={{ height: item.height }}
                />
              </MasonryItem>
            ))}
          </MasonryColumn>
        ))}
      </MasonryGrid>
    </MasonryContainer>
  );
};

export default VideoMasonrySkeletonLoader;

// ─── Layout ─────────────────────────────────────────────────────────────────

const MasonryContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  margin: 0 auto;
  padding-bottom: 16px;
`;

const MasonryGrid = styled.div`
  display: flex;
  justify-content: flex-start;
  width: 100%;
  gap: 12px;
  z-index: 4;

  @media (max-width: 768px) {
    justify-content: center;
    max-width: 100%;
    margin: 0 auto;
    gap: 8px;
  }
`;

const MasonryColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 12px;
  @media (max-width: 768px) {
    gap: 8px;
  }
`;

const MasonryItem = styled.div`
  width: 100%;
`;

/* Card shell — silhouette only, with a subtle shade pulse (no shimmer
   sweep). The card body breathes between two close water shades so a
   long wait reads as "loading" without the diagonal sweep that read as
   busy/ugly to the user. */
const SkeletonItem = styled.div`
  position: relative;
  overflow: hidden;
  border: 1px solid var(--color-line);
  /* Inset highlight uses --glass-sheen which goes fully transparent in
     dark theme, so the bright-white top edge disappears on navy. */
  box-shadow:
    0 6px 14px rgba(var(--color-brand-500-rgb), 0.12),
    inset 0 1px 0 var(--glass-sheen);
  animation: ${shadePulse} 1.8s ease-in-out infinite;

  ${(p) =>
    p.$collection
      ? css`
          border-radius: 18px;
        `
      : css`
          border-radius: 26px 30px 28px 32px / 30px 28px 32px 28px;
        `}
`;
